import type { AuthType, CredentialMode } from '#layers/data-model/utils/data-connectors/aws-iam';
import type { ConnectionStringIssue } from '#layers/data-model/utils/data-connectors/connection-string';
import * as z from 'zod/v4-mini';
import { nonEmptyString } from '#layers/base/public';
import { FormSchemaKind } from '#layers/block-engine/public';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import { PRIMITIVE_TYPES_UNSIGNED_INTEGER, SUPPORTED_PRIMITIVE_TYPES } from '#layers/data-model/constants/schema/primitive-type';
import { ConnectorType, defineConnector } from '#layers/data-model/types/data-connectors/define';
import { applyAuthToWire, applyAwsIamFieldsUnlessBase, authSubmitErrors, awsIamRefines, awsIamShape, withAwsIamSslOnOptionsChange } from '#layers/data-model/utils/data-connectors/aws-iam';
import { createConnectionStringOptionsChangeHandler, parseConnectionString } from '#layers/data-model/utils/data-connectors/connection-string';
import { caCertField, clientAuthFields, SslMode, sslModeOptions, sslSchema } from '#layers/data-model/utils/data-connectors/ssl';

/** PostgreSQL supports the full set of SSL modes, including the `prefer` fallback. */
const PG_MODES = [
	SslMode.disable,
	SslMode.prefer,
	SslMode.require,
	SslMode.verifyCa,
	SslMode.verifyFull,
];
const PG_SSL_MODE_OPTIONS = sslModeOptions(PG_MODES);
const postgresConnectionStringOptionsChangeHandler = createConnectionStringOptionsChangeHandler<PostgresOptions>({
	parse: parsePostgresConnectionString,
	invalidMessage: '$t:connectors.common.invalid_connection_string',
	fields: ['host', 'port', 'user', 'password', 'dbname', 'ssl'],
	requiredFields: ['host', 'user', 'dbname'],
});

/**
 * Zod schema for PostgreSQL options validation.
 * Uses `dbname` instead of `database` to match PostgreSQL conventions.
 * Exported for use by presets that extend this connector.
 */
export const postgresSchema = z.object({
	host: nonEmptyString(),
	port: z.number(),
	user: nonEmptyString(),
	password: z.optional(nonEmptyString()),
	dbname: nonEmptyString(),
	ssl: sslSchema({ modes: PG_MODES, clientAuth: true }),
	...awsIamShape,
}).check(...awsIamRefines);

/**
 * PostgreSQL connector options — the validated shape plus the form-only fields:
 * `connectionString` pre-fills the other fields, while `authType` and
 * `awsCredentialMode` are AWS preset discriminants that never reach the wire.
 */
export type PostgresOptions = z.infer<typeof postgresSchema> & {
	connectionString?: string;
	authType?: AuthType;
	awsCredentialMode?: CredentialMode;
};

export interface PostgresConnectionStringParseResult {
	options: Partial<PostgresOptions>;
	issues: ConnectionStringIssue[];
}

/**
 * Parse a PostgreSQL connection string into individual components.
 * Format: postgresql://user:password@host:port/database?sslmode=require
 *
 * Lenient: unescaped `%` in userinfo (common when users paste raw cloud
 * passwords) is treated as literal rather than rejected, and a warning is
 * returned so the UI can nudge the user to URL-encode.
 *
 * Exported for use by presets that extend this connector.
 */
export function parsePostgresConnectionString(connectionString: string): PostgresConnectionStringParseResult {
	const { options, issues } = parseConnectionString({
		connectionString,
		protocols: ['postgres:', 'postgresql:'],
		sslKeys: ['sslmode'],
		sslAllowedModes: PG_MODES,
	});

	return { options: options as Partial<PostgresOptions>, issues };
}

const postgresConnector = defineConnector<PostgresOptions>({
	id: 'postgres',
	name: '$t:connectors.postgres.name',
	description: '$t:connectors.postgres.description',
	type: ConnectorType.Relational,
	availableSince: '2026-01-01',

	icon: 'devicon-plain:postgresql',
	iconBackground: '#306794',
	iconForeground: '#FFFFFF',

	keywords: ['postgres', 'postgresql', 'pg', 'relational', 'sql'],

	options: ({ form, values, context }) => form(applyAwsIamFieldsUnlessBase([
		// Connection string field for quick setup
		{
			name: 'connectionString',
			label: '$t:connectors.common.connection_string',
			block: BuiltinBlockIds.InputTextarea,
			description: '$t:connectors.common.connection_string_hint',
			fieldProps: {
				layout: 'stacked',
			},
			props: {
				class: 'font-mono font-medium',
				rows: 2,
				placeholder: 'postgresql://user:password@host:5432/database',
			},
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
		},
		{
			name: 'host',
			label: '$t:common.host',
			description: '$t:connectors.common.host_description',
			block: BuiltinBlockIds.Input,
			required: true,
		},
		{
			name: 'port',
			label: '$t:common.port',
			description: '$t:connectors.common.port_description',
			block: BuiltinBlockIds.InputNumber,
			required: true,
			props: {
				formatOptions: {
					useGrouping: false,
				},
				min: 0,
				max: 65_535,
			},
			default: () => 5432,
		},
		{
			name: 'user',
			label: '$t:common.user',
			description: '$t:connectors.common.user_description',
			block: BuiltinBlockIds.Input,
			required: true,
			props: {
				autocomplete: 'off',
			},
		},
		{
			name: 'password',
			label: '$t:common.password',
			description: '$t:connectors.common.password_description',
			block: BuiltinBlockIds.InputPassword,
			required: true,
			props: {
				autocomplete: 'off',
			},
		},
		{
			name: 'dbname',
			label: '$t:connectors.common.database',
			description: '$t:connectors.common.database_description',
			block: BuiltinBlockIds.Input,
			required: true,
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
		},
		{
			name: 'ssl.mode',
			label: '$t:connectors.common.ssl_mode',
			description: '$t:connectors.common.ssl_mode_description',
			block: BuiltinBlockIds.InputSelect,
			props: {
				options: PG_SSL_MODE_OPTIONS,
			},
			default: () => 'disable',
		},
		caCertField(values),
		...clientAuthFields(values),
	], values, context?.resolvingAsBase)),

	schema: postgresSchema,

	onOptionsChange: withAwsIamSslOnOptionsChange(postgresConnectionStringOptionsChangeHandler),

	// Transform form values to postgres schema (strips connectionString and the
	// AWS preset discriminants), emitting either `password` or `aws_iam`.
	transformOptions: (opts) => applyAuthToWire(
		{ host: opts.host, port: opts.port, user: opts.user, dbname: opts.dbname, ssl: opts.ssl },
		opts,
	),

	validateOptions: (values) => authSubmitErrors(values),

	supportedPrimitiveTypes: [...(new Set(SUPPORTED_PRIMITIVE_TYPES).difference(new Set(PRIMITIVE_TYPES_UNSIGNED_INTEGER)))],
});

export default postgresConnector;
